/**
 * Light Parsers for Quick Create Commands
 *
 * Uses chrono-node for date parsing and regex for contact info extraction.
 * No AI involved - designed for speed (~20-50ms).
 */

import * as chrono from 'chrono-node';

// ============================================================================
// Date/Time Parsing
// ============================================================================

export interface ParsedDate {
  date: Date;
  text: string; // The text that was parsed (to remove from title)
}

/**
 * Parse natural language date/time from text
 * Returns the parsed date and the matched text fragment
 */
export function parseDate(text: string, referenceDate?: Date): ParsedDate | null {
  const results = chrono.parse(text, referenceDate || new Date(), { forwardDate: true });

  if (results.length === 0) {
    return null;
  }

  const result = results[0];
  return {
    date: result.start.date(),
    text: result.text,
  };
}

/**
 * Parse date and return ISO string for API
 */
export function parseDateToISO(text: string): { isoDate: string; matchedText: string } | null {
  const parsed = parseDate(text);
  if (!parsed) return null;

  return {
    isoDate: parsed.date.toISOString(),
    matchedText: parsed.text,
  };
}

export function removeDateFromTitle(title: string, matchedText: string): string {
  return title
    .replace(matchedText, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ============================================================================
// Contact Info Extraction
// ============================================================================

// Email: require valid TLD (2+ letters) to avoid false matches like "groceries@5pm"
const EMAIL_REGEX = /[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}/g;

// Phone: loose pattern for common formats (7+ digits with separators)
// Handles: 555-1234, (555) 123-4567, +1 555 123 4567
const PHONE_REGEX = /(?:\+?\d{1,3}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{2,4}[-.\s]?\d{2,4}/g;

export interface ParsedContactInfo {
  name: string;
  emails: string[];
  phones: string[];
}

/**
 * Extract contact information from text
 * Returns name (remaining text) and any emails/phones found
 */
export function parseContactInfo(text: string): ParsedContactInfo {
  const emails = text.match(EMAIL_REGEX) || [];
  const phones = text.match(PHONE_REGEX) || [];

  // Strip the matched emails/phones; what remains is the name.
  let name = text;
  for (const email of emails) {
    name = name.replace(email, '');
  }
  for (const phone of phones) {
    name = name.replace(phone, '');
  }
  name = name.replace(/\s+/g, ' ').trim();

  return {
    name: name || 'Unnamed Contact',
    emails,
    phones,
  };
}

// ============================================================================
// Priority/Status Keywords
// ============================================================================

const PRIORITIES: Record<string, string> = {
  'urgent': 'high',
  'important': 'high',
  'high': 'high',
  'low': 'low',
  'normal': 'normal',
};

const STATUSES: Record<string, string> = {
  'done': 'done',
  'complete': 'done',
  'completed': 'done',
  'finished': 'done',
  'todo': 'pending',
  'pending': 'pending',
};

export interface ParsedKeywords {
  priority?: string;
  status?: string;
  cleanedText: string;
}

/**
 * Extract priority and status keywords from text
 */
export function parseKeywords(text: string): ParsedKeywords {
  let cleanedText = text;
  let priority: string | undefined;
  let status: string | undefined;

  for (const [keyword, value] of Object.entries(PRIORITIES)) {
    const regex = new RegExp(`\\b${keyword}\\b:?\\s*`, 'i');
    if (regex.test(cleanedText)) {
      priority = value;
      cleanedText = cleanedText.replace(regex, '');
      break;
    }
  }

  for (const [keyword, value] of Object.entries(STATUSES)) {
    const regex = new RegExp(`\\b${keyword}\\b:?\\s*`, 'i');
    if (regex.test(cleanedText)) {
      status = value;
      cleanedText = cleanedText.replace(regex, '');
      break;
    }
  }

  return {
    priority,
    status,
    cleanedText: cleanedText.replace(/\s+/g, ' ').trim(),
  };
}

// ============================================================================
// Task Parsing
// ============================================================================

export interface ParsedTask {
  title: string;
  dueAt?: string; // ISO date string
  priority?: string;
}

/**
 * Parse task input: "/task Buy groceries tomorrow"
 */
export function parseTaskInput(input: string): ParsedTask {
  const { priority, cleanedText } = parseKeywords(input);
  const dateResult = parseDateToISO(cleanedText);

  let title = cleanedText;
  let dueAt: string | undefined;

  if (dateResult) {
    title = removeDateFromTitle(cleanedText, dateResult.matchedText);
    dueAt = dateResult.isoDate;
  }

  return {
    title: title || 'Untitled Task',
    dueAt,
    priority,
  };
}

// ============================================================================
// Event Parsing
// ============================================================================

export interface ParsedEvent {
  title: string;
  startAt?: string; // ISO date string
  endAt?: string; // ISO date string
}

/**
 * Parse event input: "/event Team standup tomorrow at 9am"
 */
export function parseEventInput(input: string): ParsedEvent {
  const results = chrono.parse(input, new Date(), { forwardDate: true });

  let title = input;
  let startAt: string | undefined;
  let endAt: string | undefined;

  if (results.length > 0) {
    const result = results[0];
    startAt = result.start.date().toISOString();

    if (result.end) {
      endAt = result.end.date().toISOString();
    } else {
      // Default to 1 hour duration
      const end = new Date(result.start.date());
      end.setHours(end.getHours() + 1);
      endAt = end.toISOString();
    }

    title = removeDateFromTitle(input, result.text);
  }

  return {
    title: title || 'Untitled Event',
    startAt,
    endAt,
  };
}

// ============================================================================
// Note Parsing (simple - just title)
// ============================================================================

export interface ParsedNote {
  title: string;
  content?: string;
}

/**
 * Parse note input - mostly just title extraction
 * If very long, split into title and content
 */
export function parseNoteInput(input: string): ParsedNote {
  const MAX_TITLE_LENGTH = 100;

  if (input.length <= MAX_TITLE_LENGTH) {
    return { title: input || 'Untitled Note' };
  }

  const firstLine = input.split('\n')[0];
  if (firstLine.length <= MAX_TITLE_LENGTH) {
    return {
      title: firstLine,
      content: input.slice(firstLine.length).trim(),
    };
  }

  // Truncate at word boundary
  const truncated = input.slice(0, MAX_TITLE_LENGTH);
  const lastSpace = truncated.lastIndexOf(' ');
  const title = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;

  return {
    title,
    content: input.slice(title.length).trim(),
  };
}
